<template>
  <div class="achievement-chart-container">
    <!-- Horizontal Stacked Bar Chart -->
    <div v-if="chartType === 'bar'" class="horizontal-chart">
      <div class="chart-header">
        <h6 class="chart-title">Activity Performance Overview</h6>
        <div class="chart-subtitle">Distance, Points, and Activity Count</div>
      </div>
      <div class="chart-content">
        <div class="chart-bars">
          <div
            v-for="(stat, type) in stats"
            :key="type"
            class="chart-row"
          >
            <div class="row-header">
              <div class="activity-icon">
                <font-awesome-icon
                  :icon="getActivityIcon(type)"
                  :class="['activity-icon', getActivityClass(type)]"
                />
              </div>
              <div class="activity-info">
                <div class="activity-name">{{ type }}</div>
                <div class="activity-count">{{ stat.count }} activities</div>
              </div>
            </div>
            <div class="row-chart">
              <div class="chart-bar-container">
                <div
                  class="chart-bar distance-bar"
                  :style="{
                    width: getBarWidth(stat.km, 'distance') + '%',
                    backgroundColor: getActivityColor(type)
                  }"
                  :title="`Distance: ${stat.km.toFixed(2)} km`"
                >
                  <span class="bar-label">{{ stat.km.toFixed(1) }} km</span>
                </div>
                <div
                  class="chart-bar points-bar"
                  :style="{
                    width: getBarWidth(stat.point, 'points') + '%',
                    backgroundColor: getActivityColor(type) + '80'
                  }"
                  :title="`Points: ${stat.point}`"
                >
                  <span class="bar-label">{{ stat.point }} pts</span>
                </div>
                <div
                  class="chart-bar blocks-bar"
                  :style="{
                    width: getBarWidth(stat.block, 'blocks') + '%',
                    backgroundColor: getActivityColor(type) + '40'
                  }"
                  :title="`Blocks: ${stat.block}`"
                >
                  <span class="bar-label">{{ stat.block }} blk</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="chart-legend">
          <div class="legend-item">
            <div class="legend-color distance"></div>
            <span>Distance (km)</span>
          </div>
          <div class="legend-item">
            <div class="legend-color points"></div>
            <span>Points</span>
          </div>
          <div class="legend-item">
            <div class="legend-color blocks"></div>
            <span>Blocks</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Progress Ring Chart -->
    <div v-else-if="chartType === 'doughnut'" class="progress-chart">
      <div class="chart-header">
        <h6 class="chart-title">Achievement Progress</h6>
        <div class="chart-subtitle">Overall Performance Metrics</div>
      </div>
      <div class="chart-content" :class="{ 'doughnut-active': chartType === 'doughnut' }">
        <div class="progress-rings">
          <div class="ring-group">
            <div class="ring-container">
              <svg class="ring-svg" viewBox="0 0 120 120">
                <circle
                  cx="60"
                  cy="60"
                  r="45"
                  fill="none"
                  stroke="#e9ecef"
                  stroke-width="8"
                />
                <circle
                  cx="60"
                  cy="60"
                  r="45"
                  fill="none"
                  stroke="#4a90e2"
                  stroke-width="8"
                  :stroke-dasharray="totalDistanceCircumference"
                  :stroke-dashoffset="totalDistanceOffset"
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div class="ring-center">
                <div class="ring-value">{{ totalDistance.toFixed(1) }}</div>
                <div class="ring-label">Total km</div>
              </div>
            </div>
            <div class="ring-title">Distance</div>
          </div>

          <div class="ring-group">
            <div class="ring-container">
              <svg class="ring-svg" viewBox="0 0 120 120">
                <circle
                  cx="60"
                  cy="60"
                  r="45"
                  fill="none"
                  stroke="#e9ecef"
                  stroke-width="8"
                />
                <circle
                  cx="60"
                  cy="60"
                  r="45"
                  fill="none"
                  stroke="#2ecc71"
                  stroke-width="8"
                  :stroke-dasharray="totalPointsCircumference"
                  :stroke-dashoffset="totalPointsOffset"
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div class="ring-center">
                <div class="ring-value">{{ totalPoints }}</div>
                <div class="ring-label">Total pts</div>
              </div>
            </div>
            <div class="ring-title">Points</div>
          </div>

          <div class="ring-group">
            <div class="ring-container">
              <svg class="ring-svg" viewBox="0 0 120 120">
                <circle
                  cx="60"
                  cy="60"
                  r="45"
                  fill="none"
                  stroke="#e9ecef"
                  stroke-width="8"
                />
                <circle
                  cx="60"
                  cy="60"
                  r="45"
                  fill="none"
                  stroke="#ff6b35"
                  stroke-width="8"
                  :stroke-dasharray="totalActivitiesCircumference"
                  :stroke-dashoffset="totalActivitiesOffset"
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div class="ring-center">
                <div class="ring-value">{{ totalActivities }}</div>
                <div class="ring-label">Activities</div>
              </div>
            </div>
            <div class="ring-title">Count</div>
          </div>
        </div>

        <div class="activity-breakdown">
          <h6 class="breakdown-title">Activity Breakdown</h6>
          <div class="breakdown-items">
            <div
              v-for="(stat, type) in stats"
              :key="type"
              class="breakdown-item"
            >
              <div class="breakdown-icon">
                <font-awesome-icon
                  :icon="getActivityIcon(type)"
                  :class="['activity-icon', getActivityClass(type)]"
                />
              </div>
              <div class="breakdown-info">
                <div class="breakdown-name">{{ type }}</div>
                <div class="breakdown-stats">
                  {{ stat.count }} • {{ stat.km.toFixed(1) }}km • {{ stat.point }}pts
                </div>
              </div>
              <div class="breakdown-percentage">
                {{ ((stat.km / totalDistance) * 100).toFixed(1) }}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AchievementChart',
  props: {
    stats: {
      type: Object,
      required: true
    },
    chartType: {
      type: String,
      default: 'bar',
      validator: value => ['bar', 'doughnut'].includes(value)
    }
  },
  computed: {
    totalDistance () {
      return Object.values(this.stats).reduce((sum, stat) => sum + stat.km, 0)
    },
    totalPoints () {
      return Object.values(this.stats).reduce((sum, stat) => sum + stat.point, 0)
    },
    totalActivities () {
      return Object.values(this.stats).reduce((sum, stat) => sum + stat.count, 0)
    },
    totalDistanceCircumference () {
      return 2 * Math.PI * 45
    },
    totalDistanceOffset () {
      return this.totalDistanceCircumference - (this.totalDistance / 100) * this.totalDistanceCircumference
    },
    totalPointsCircumference () {
      return 2 * Math.PI * 45
    },
    totalPointsOffset () {
      return this.totalPointsCircumference - (this.totalPoints / 1000) * this.totalPointsCircumference
    },
    totalActivitiesCircumference () {
      return 2 * Math.PI * 45
    },
    totalActivitiesOffset () {
      return this.totalActivitiesCircumference - (this.totalActivities / 50) * this.totalActivitiesCircumference
    }
  },
  methods: {
    getActivityColor (type) {
      const colors = {
        Swim: '#00b4d8',
        Ride: '#06ffa5',
        Run: '#ff6b35',
        Walk: '#7209b7'
      }
      return colors[type] || '#4a90e2'
    },
    getActivityIcon (type) {
      const icons = {
        Swim: 'swimmer',
        Ride: 'biking',
        Run: 'running',
        Walk: 'walking'
      }
      return icons[type] || 'question'
    },
    getActivityClass (type) {
      const classes = {
        Swim: 'swim',
        Ride: 'bike',
        Run: 'run',
        Walk: 'walk'
      }
      return classes[type] || 'default'
    },
    getBarWidth (value, type) {
      const maxValues = {
        distance: Math.max(...Object.values(this.stats).map(s => s.km)),
        points: Math.max(...Object.values(this.stats).map(s => s.point)),
        blocks: Math.max(...Object.values(this.stats).map(s => s.block))
      }
      return Math.max((value / maxValues[type]) * 100, 3)
    }
  }
}
</script>

<style lang="scss" scoped>
.achievement-chart-container {
  position: relative;
  height: 300px;
  width: 100%;
  margin: 1rem 0;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(74, 144, 226, 0.1);
}

.chart-header {
  margin-bottom: 1rem;
  text-align: center;
}

.chart-title {
  color: #2c3e50;
  font-weight: 600;
  margin: 0;
  font-size: 0.9rem;
}

.chart-subtitle {
  color: #7f8c8d;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.chart-content {
  position: relative;
  height: calc(100% - 60px);
  display: flex;
  flex-direction: column;
  background: #f7fbff; /* Light blue for contrast */
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(74, 144, 226, 0.08); /* Subtle blue shadow */
  border: 1px solid rgba(74, 144, 226, 0.10);
  padding: 1.25rem 1rem;
  min-height: 350px;
}

.doughnut-active::after {
  /* Removed blur overlay to prevent hiding content */
}

.chart-content.doughnut-active {
  background: rgba(247, 251, 255, 0.85); /* semi-transparent for frosted effect */
}

// Horizontal Stacked Bar Chart Styles
.horizontal-chart {
  height: 100%;
  min-height: 350px;
}

.chart-bars {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
  overflow-y: auto;
}

.chart-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: rgba(74, 144, 226, 0.02);
  border-radius: 8px;
  border: 1px solid rgba(74, 144, 226, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(74, 144, 226, 0.05);
    transform: translateX(4px);
  }
}

.row-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 140px;
  flex-shrink: 0;
}

.activity-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .activity-icon {
    font-size: 0.9rem;

    &.swim { color: #00b4d8; }
    &.bike { color: #06ffa5; }
    &.run { color: #ff6b35; }
    &.walk { color: #7209b7; }
  }
}

.activity-info {
  flex: 1;
}

.activity-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}

.activity-count {
  font-size: 0.7rem;
  color: #7f8c8d;
}

.row-chart {
  flex: 1;
  min-width: 0;
}

.chart-bar-container {
  display: flex;
  height: 32px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.chart-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.3s ease;
  cursor: pointer;
  min-width: 30px;

  &:hover {
    transform: scaleY(1.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
}

.bar-label {
  font-size: 0.65rem;
  font-weight: 600;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
  z-index: 1;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #2c3e50;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;

  &.distance {
    background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  }

  &.points {
    background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
    opacity: 0.8;
  }

  &.blocks {
    background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
    opacity: 0.4;
  }
}

// Progress Ring Chart Styles
.progress-chart {
  height: 100%;
}

.progress-rings {
  display: flex;
  justify-content: space-around;
  margin-bottom: 1.5rem;
  padding: 1rem 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(74, 144, 226, 0.08);
  border: 1px solid rgba(74, 144, 226, 0.10);
}

.ring-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.ring-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ring-svg {
  width: 120px;
  height: 120px;
}

.ring-center {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.ring-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
}

.ring-label {
  font-size: 0.65rem;
  color: #7f8c8d;
  margin-top: 0.25rem;
}

.ring-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: #2c3e50;
  text-align: center;
}

.activity-breakdown {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  padding: 1rem;
  border: 1.5px solid rgba(74, 144, 226, 0.18);
  box-shadow: 0 4px 16px rgba(74, 144, 226, 0.10);
  z-index: 2;
  position: relative;
}

.breakdown-title {
  color: #2c3e50;
  font-weight: 600;
  margin: 0 0 1rem 0;
  font-size: 0.85rem;
}

.breakdown-items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.breakdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  background: white;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

.breakdown-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  .activity-icon {
    font-size: 0.8rem;

    &.swim { color: #00b4d8; }
    &.bike { color: #06ffa5; }
    &.run { color: #ff6b35; }
    &.walk { color: #7209b7; }
  }
}

.breakdown-info {
  flex: 1;
}

.breakdown-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.8rem;
  margin-bottom: 0.2rem;
}

.breakdown-stats {
  font-size: 0.7rem;
  color: #7f8c8d;
}

.breakdown-percentage {
  font-weight: 600;
  color: #4a90e2;
  font-size: 0.8rem;
  background: rgba(74, 144, 226, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
}

@media (max-width: 768px) {
  .achievement-chart-container {
    height: 250px;
    padding: 0.5rem;
  }

  .chart-title {
    font-size: 0.8rem;
  }

  .chart-subtitle {
    font-size: 0.7rem;
  }

  .chart-content {
    min-height: 250px;
  }

  .chart-row {
    padding: 0.5rem;
    gap: 0.75rem;
  }

  .row-header {
    min-width: 120px;
    gap: 0.5rem;
  }

  .activity-icon {
    width: 28px;
    height: 28px;

    .activity-icon {
      font-size: 0.8rem;
    }
  }

  .activity-name {
    font-size: 0.8rem;
  }

  .activity-count {
    font-size: 0.65rem;
  }

  .chart-bar-container {
    height: 28px;
  }

  .bar-label {
    font-size: 0.6rem;
  }

  .chart-legend {
    gap: 1rem;
  }

  .legend-item {
    font-size: 0.7rem;
  }

  .progress-rings {
    gap: 0.5rem;
  }

  .ring-svg {
    width: 70px;
    height: 70px;
  }

  .ring-value {
    font-size: 0.8rem;
  }

  .ring-label {
    font-size: 0.5rem;
  }

  .ring-title {
    font-size: 0.6rem;
  }

  .activity-breakdown {
    padding: 0.75rem;
  }

  .breakdown-title {
    font-size: 0.8rem;
  }

  .breakdown-item {
    padding: 0.4rem;
    gap: 0.5rem;
  }

  .breakdown-icon {
    width: 24px;
    height: 24px;

    .activity-icon {
      font-size: 0.7rem;
    }
  }

  .breakdown-name {
    font-size: 0.75rem;
  }

  .breakdown-stats {
    font-size: 0.65rem;
  }

  .breakdown-percentage {
    font-size: 0.75rem;
    padding: 0.2rem 0.4rem;
  }
  .horizontal-chart {
    min-height: 250px;
  }
}
</style>
